import compact from 'lodash/compact';
import map from 'lodash/map';
import zipObject from 'lodash/zipObject';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import {
  EncounterSettings,
} from 'pages/Dashboard/pages/Encounters/types/settings';
import {
  useGetAppointmentTypes,
} from 'views/EMR/Settings/hooks';
import {
  assignAppointmentTypeColors,
} from 'views/EMR/Settings/helper';
import isNil from 'lodash/isNil';
import {
  getColorForString,
} from 'utils/colors';
import {
  useServiceFacilities,
} from 'views/EMR/stores/serviceFacility';
import {
  useAppointmentStatusOptions,
} from 'views/EMR/stores/appointmentStatus';
import {
  AppointmentStatusFilterOption,
} from 'views/EMR/stores/appointmentStatusHelpers';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';

export const colorClasses = Object.freeze([
  '!bg-populate-visit-1',
  '!bg-populate-visit-2',
  '!bg-populate-visit-3',
  '!bg-populate-visit-4',
  '!bg-populate-visit-5',
  '!bg-populate-visit-6',
  '!bg-populate-visit-7',
  '!bg-populate-visit-8',
  '!bg-populate-visit-9',
  '!bg-populate-visit-10',
  '!bg-populate-visit-11',
  '!bg-populate-visit-12',
  '!bg-populate-visit-13',
  '!bg-populate-visit-14',
  '!bg-populate-visit-15',
  '!bg-teal-200',
  '!bg-cyan-200',
  '!bg-emerald-200',
  '!bg-fuchsia-200',
  '!bg-lime-200',
  '!bg-green-200',
  '!bg-indigo-200',
  '!bg-sky-200',
  '!bg-teal-300',
  '!bg-cyan-300',
  '!bg-emerald-300',
  '!bg-fuchsia-300',
  '!bg-lime-300',
  '!bg-green-300',
  '!bg-indigo-300',
  '!bg-sky-300',
  '!bg-teal-400',
  '!bg-cyan-400',
  '!bg-emerald-400',
  '!bg-fuchsia-400',
  '!bg-lime-400',
  '!bg-green-400',
  '!bg-indigo-400',
  '!bg-sky-400',
  '!bg-teal-500',
  '!bg-cyan-500',
  '!bg-emerald-500',
  '!bg-fuchsia-500',
  '!bg-lime-500',
  '!bg-green-500',
  '!bg-indigo-500',
  '!bg-sky-500',
  '!bg-populate-pastel-1',
  '!bg-populate-pastel-2',
  '!bg-populate-pastel-3',
  '!bg-populate-pastel-4',
  '!bg-populate-pastel-5',
  '!bg-populate-pastel-6',
  '!bg-populate-pastel-7',
  '!bg-populate-pastel-8',
  '!bg-populate-pastel-9',
  '!bg-populate-pastel-10',
  '!bg-populate-pastel-11',
  '!bg-populate-pastel-12',
  '!bg-populate-pastel-13',
  '!bg-populate-pastel-14',
  '!bg-populate-pastel-15',
  '!bg-populate-pastel-16',
  '!bg-populate-pastel-17',
  '!bg-populate-pastel-18',
  '!bg-populate-pastel-19',
  '!bg-populate-pastel-20',
  '!bg-populate-pastel-21',
  '!bg-populate-pastel-22',
  '!bg-populate-pastel-23',
  '!bg-populate-pastel-24',
  '!bg-populate-pastel-25',
  '!bg-populate-pastel-26',
  '!bg-populate-pastel-27',
  '!bg-populate-pastel-28',
  '!bg-populate-pastel-29',
  '!bg-populate-pastel-30',
]);

export const pastelColors = Object.freeze([
  '#5F7F89', // darker slate blue
  '#D28E60', // warm tan-orange
  '#A479A0', // muted mauve
  '#8A8558', // olive gray
  '#7B89B8', // muted lavender-blue
  '#6FA584', // forest mint
  '#4C7391', // steel blue
  '#4E9E60', // earthy green
  '#C06565', // rose red
  '#4E6E60', // deep sage
  '#7A8F49', // olive green
  '#926C92', // darker thistle
  '#6354A6', // indigo violet
  '#4A8289', // sea teal
  '#D6933E', // darker peach
  '#4D7A95', // denim blue
  '#7A8F5B', // olive green-gray
  '#A25463', // crimson rose
  '#557CA0', // muted sky
  '#8E5D42', // sienna brown
  '#7743C1', // violet purple
  '#5B548A', // muted indigo
  '#527A55', // woodland green
  '#7C6652', // taupe brown
  '#9C5C69', // dusty pink
  '#5E8F68', // forest green
  '#A1873D', // mustard gold
  '#4D8579', // teal green
  '#A19B46', // ochre yellow
  '#A4566F', // dark rose
]);

export const visitTypeColors = Object.freeze([
  ...colorClasses,
]);

const serviceFacilityColorsClasses = Object.freeze([
  '!bg-orange-700',
  '!bg-sky-700',
  '!bg-fuchsia-700',
  '!bg-lime-700',
  '!bg-teal-700',
  '!bg-violet-700',
  '!bg-rose-700',
  '!bg-green-700',
  '!bg-amber-700',
  '!bg-emerald-700',
  '!bg-blue-700',
  '!bg-cyan-700',
  '!bg-indigo-700',
  '!bg-pink-700',
  '!bg-purple-700',
  '!bg-red-700',
  '!bg-slate-700',
  '!bg-yellow-700',
]);

const claimStatusTypeColors = Object.freeze({
  'Not Started': '!bg-stone-200 !text-stone-600',
  'Out-of-pocket': '!bg-brown-100 !text-brown-600',
  Draft: '!bg-slate-200 !text-slate-600',
  Hold: '!bg-orange-200 !text-orange-900',
  Validated: '!bg-teal-100 !text-teal-800',
  Accepted: '!bg-blue-100 !text-blue-700',
  Submitted: '!bg-lime-100 !text-lime-700',
  Paid: '!bg-green-200 !text-green-700',
  Posted: '!bg-indigo-100 !text-indigo-700',
  Denied: '!bg-red-100 !text-red-700',
  Rejected: '!bg-orange-100 !text-orange-700',
  Voided: '!bg-gray-200 !text-gray-500',
  Archived: '!bg-zinc-200 !text-zinc-600',
});

const ledgerCategoryColors = Object.freeze({
  'Primary Claim': '!bg-indigo-100 !text-indigo-700',
  'Secondary Claim': '!bg-orange-200 !text-orange-900',
  EOB: '!bg-teal-100 !text-teal-800',
  'Custom Charge': '!bg-blue-100 !text-blue-700',
  'Copay Allocation': '!bg-cyan-100 !text-cyan-700',
  'Copay Paid': '!bg-sky-100 !text-sky-700',
  'Patient Payment': '!bg-purple-100 !text-purple-700',
  'Credit Allocation': '!bg-purple-100 !text-purple-700',
  'Write Off': '!bg-fuchsia-100 !text-fuchsia-700',
  'Moved to Credit': '!bg-teal-100 !text-teal-700',
  Refund: '!bg-red-100 !text-red-700',
  Payment: '!bg-green-100 !text-green-700',
});

const miscColors = Object.freeze({
  amountTag: 'bg-gray-50 text-gray-600',
  customService: 'text-black bg-gray-100',
  terminal: 'bg-white text-gray-500',
  task: 'text-gray-700',
  eobPosted: 'bg-sky-100 text-sky-500 !border-sky-300',
  eobNotPosted: 'bg-white text-gray-500',
  Ready: '!bg-emerald-100 !text-emerald-700',
  InProgress: '!bg-stone-200 !text-stone-600',
  Failed: '!bg-red-100 !text-red-700',
  common: 'bg-blue-100 !text-blue-500',
  Crossover: '!bg-orange-100 !text-orange-700',
});

const rtmPriorityColors = Object.freeze({
  'Needs Review': '!bg-red-100 !text-red-700',
  Stable: '!bg-emerald-100 !text-emerald-700',
  Watch: '!bg-amber-100 !text-amber-700',
});

export function useEncounterSettings(): EncounterSettings {
  const { data: doctors } = useGetUsers({
    onlyDoctors: true,
    sortBySelf: true,
  });
  const serviceFacility = useServiceFacilities();

  const { appointmentTypes, durationByType, isTelehealthByType } = useGetAppointmentTypes();

  return {
    doctors: (doctors ?? []).map((doctor) => ({
      name: getDisplayName(doctor.formattedNames),
      indexByLastName: doctor.formattedNames?.indexByLastName ?? '',
      indexByLastNameWithCredentials: doctor.formattedNames?.indexByLastNameWithCredentials ?? '',
      initials: doctor.formattedNames?.initials ?? '',
      userId: doctor.userId ?? 0,
    })),
    appointmentTypes,
    durationByType,
    isTelehealthByType,
    serviceFacility: serviceFacility ?? [],
  };
}

type EncounterFilterMeta = EncounterSettings & {
  appointmentStatuses: AppointmentStatusFilterOption[];
};

export function useEncounterFilterMeta(): EncounterFilterMeta {
  const encounterSettings = useEncounterSettings();
  const appointmentStatuses = useAppointmentStatusOptions();

  return {
    ...encounterSettings,
    appointmentStatuses,
  };
}

function useAppointmentTypeColors(): Record<string, string> {
  const { appointmentTypes } = useGetAppointmentTypes();
  return assignAppointmentTypeColors(appointmentTypes);
}

export const fixedAppointmentStatusColors: Record<string, string> = {
  'Confirmation Pending': '#D97706', // dark amber
  Confirmed: '#15803D', // dark green
  Cancelled: '#DC2626', // strong red
  'No Show': '#C2410C', // burnt orange
  'Reschedule Requested': '#1D4ED8', // deep blue
  'Waiting room': '#7E22CE', // deep purple
  Ready: '#047857', // dark emerald
  'Checked Out': '#374151', // dark gray
  'Billing Sent': '#0369A1', // dark sky
  'Note Completed': '#0D9488', // deep teal
  'Note Finalized': '#4338CA', // dark indigo
};

export function useAppointmentStatusColors(): Record<string, string> {
  const appointmentStatuses = useAppointmentStatusOptions();

  const customStatuses = appointmentStatuses?.filter(
    ({ patientAppointmentStatus }) => isNil(fixedAppointmentStatusColors[patientAppointmentStatus]),
  ).map(({ patientAppointmentStatus }) => patientAppointmentStatus) ?? [];

  const statusColors = zipObject(
    customStatuses,
    customStatuses.map(
      (type, index) => getColorForString(type, pastelColors),
    ),
  );

  return {
    ...statusColors,
    ...fixedAppointmentStatusColors,
  };
}

export function useServiceFacilityColors(): Record<string, string> {
  const serviceFacilitiesData = useServiceFacilities();
  const serviceFacilities = compact(map(serviceFacilitiesData ?? [], 'nickname'));
  return zipObject(
    serviceFacilities,
    serviceFacilities.map(
      (type, index) => serviceFacilityColorsClasses[index % serviceFacilities.length],
    ),
  );
}

const faxColors: Record<string, string> = Object.freeze({
  New: '!bg-blue-100',
  Read: '!bg-blue-300',
  Archived: '!bg-gray-200',
  Sent: '!bg-green-200',
  Failed: '!bg-red-300',
});

export const ledgerColors = Object.freeze({
  netCharges: 'bg-blue-400 text-blue-400',
  insurancePaid: 'bg-teal-400 text-teal-400',
  insuranceBalance: 'bg-green-400 text-green-400',
  patientPaid: 'bg-violet-400 text-violet-400',
  patientBalance: 'bg-pink-400 text-pink-400',
  patientCredit: 'bg-lime-500 text-lime-500',
  patientDue: 'bg-red-400 text-red-400',
});

export const phoneNumberTagColors = Object.freeze({
  Invalid: '!bg-red-100 !text-red-700',
  Landline: '!bg-amber-100 !text-amber-700',
  Mobile: '!bg-indigo-100 !text-indigo-700',
  Unknown: '!bg-slate-200 !text-slate-600',
});

export function useColorsHashMap(): Record<string, string> {
  const appointmentTypeColors = useAppointmentTypeColors();
  const serviceFacilityColors = useServiceFacilityColors();

  return {
    ...miscColors,
    ...appointmentTypeColors,
    ...serviceFacilityColors,
    ...claimStatusTypeColors,
    ...faxColors,
    ...ledgerColors,
    ...ledgerCategoryColors,
    ...phoneNumberTagColors,
    ...rtmPriorityColors,
  };
}
